import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Redirect } from "react-router-dom";
import { TbSearch, TbUserPlus } from "react-icons/tb";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPlatformCreateAccess } from "../../../../Hooks/useBaasPlatformCreateAccess";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { BaasTablePageSkeleton } from "../components/BaasSkeleton";
import { aggregateBaasUsersByPerson, fetchBaasUsers } from "../../../../Services/baasUsersApi";
import { isBaasHmlInstance } from "../../../../Services/baasInstancesApi";
import { BaasLoggedInDot } from "../components/BaasRowActionMenu";
import { GridHistoryDetailButton } from "../../../../Components/Molecules/GridHistory";
import { BaasUserCreateModal } from "./components/BaasUserCreateModal";
import { resolveBaasHomologPair } from "../Instances/baasHomologacao";
import { BAAS_BASE_PATH } from "../constants";
import { maskTaxIdForList } from "../../../../Utils/masks";

const roleLabelKey = {
  super_user: "baas.users.role.super_user",
  admin: "baas.users.role.admin",
  operator: "baas.users.role.operator",
  viewer: "baas.users.role.viewer",
};

const statusStyles = {
  active: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
  invited: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
  suspended: "bg-gray-100 text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-400",
};

const accessStatusStyles = {
  completed: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
  pending: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
  no_email: "bg-gray-100 text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-400",
};

const formatLastAccess = (value) => {
  if (!value) {
    return intl.get("baas.users.neverAccessed");
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
};

const formatDocumentDisplay = (value) => maskTaxIdForList(value);

const primaryStatus = (memberships = []) => {
  if (memberships.some((m) => m.status === "active")) return "active";
  if (memberships.some((m) => m.status === "invited")) return "invited";
  return "suspended";
};

const primaryRole = (memberships = []) => {
  if (memberships.some((m) => m.role === "super_user")) return "super_user";
  if (memberships.some((m) => m.role === "admin")) return "admin";
  if (memberships.some((m) => m.role === "operator")) return "operator";
  return memberships[0]?.role ?? "viewer";
};

/**
 * @param {{ scope?: "org"|"instance" }} props
 */
export const BaasUsersPage = ({ scope = "org" }) => {
  const isInstanceScope = scope === "instance";
  const { instances, selectedInstance, selectInstance, clearSelectedInstance } =
    useBaasInstance();
  const instancesKey = instances.map((item) => item.id).join("|");
  const pageKey = isInstanceScope
    ? selectedInstance?.id || "instance-users"
    : instancesKey || "org-users";
  const isLoading = useBaasPageLoading(pageKey);
  const { can, isMaster: capsMaster, loading: capsLoading } = useBaasCapabilities();
  const {
    isMaster: platformMaster,
    isValutConfigAdmin,
    loading: platformLoading,
  } = useBaasPlatformCreateAccess();
  const isMaster = Boolean(capsMaster || platformMaster);
  const [searchQuery, setSearchQuery] = useState("");
  /** Org scope: filter by selection card id. Empty = all instances. */
  const [instanceFilterId, setInstanceFilterId] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [people, setPeople] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [manageSupported, setManageSupported] = useState(false);

  const isHmlLane =
    isInstanceScope && isBaasHmlInstance(selectedInstance);
  const homologPair = useMemo(
    () =>
      isInstanceScope
        ? resolveBaasHomologPair(instances, selectedInstance)
        : { production: null, development: null },
    [isInstanceScope, instances, selectedInstance],
  );
  // Trust MS ManageSupported for org; instance scope also requires users.manage capability.
  // Do NOT use !isReadOnly — Operador has transactions.write so isReadOnly=false and was
  // wrongly allowed to open Usuários / see Editar when ManageSupported was stale.
  // HML: carteira login is always Prod — list is read-only (same Prod users).
  const canManageUsers =
    !isHmlLane &&
    (isMaster ||
      (Boolean(manageSupported) &&
        (!isInstanceScope || capsLoading || can("users.manage"))));

  // Org multi-instance create/link is master-only; scoped admins create via instance Users page.
  const canCreateUsers = canManageUsers && (isInstanceScope || isMaster);

  // Org /baas/usuarios = Valut matriz only (sidebar already hides; enforce route too).
  if (!isInstanceScope && !platformLoading && !isValutConfigAdmin) {
    return <Redirect to={BAAS_BASE_PATH} />;
  }

  const instanceNameById = useMemo(
    () => Object.fromEntries(instances.map((instance) => [instance.id, instance.name])),
    [instances],
  );

  useEffect(() => {
    if (!isInstanceScope && selectedInstance) {
      clearSelectedInstance();
    }
  }, [isInstanceScope, selectedInstance, clearSelectedInstance]);

  useEffect(() => {
    if (!isHmlLane) return undefined;
    setCreateModalOpen(false);
    setEditingPerson(null);
    return undefined;
  }, [isHmlLane]);

  useEffect(() => {
    let cancelled = false;

    if (isInstanceScope) {
      if (!selectedInstance) {
        setPeople([]);
        setManageSupported(false);
        setLoadError(null);
        setUsersLoading(false);
        return undefined;
      }

      const instance = selectedInstance;
      setUsersLoading(true);
      setLoadError(null);
      (async () => {
        try {
          const result = await fetchBaasUsers(instance);
          if (!cancelled) {
            setPeople(aggregateBaasUsersByPerson(result.items));
            setManageSupported(Boolean(result.manageSupported));
            setLoadError(null);
          }
        } catch (error) {
          if (!cancelled) {
            setPeople([]);
            setManageSupported(false);
            setLoadError(
              error?.detail || error?.message || intl.get("baas.users.loadError"),
            );
          }
        } finally {
          if (!cancelled) setUsersLoading(false);
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    if (!instancesKey) {
      setPeople([]);
      setManageSupported(false);
      setLoadError(null);
      setUsersLoading(false);
      return undefined;
    }

    const orgInstances = instances;
    setUsersLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const result = await fetchBaasUsers(null, { instances: orgInstances });
        if (!cancelled) {
          setPeople(aggregateBaasUsersByPerson(result.items));
          setManageSupported(Boolean(result.manageSupported));
          setLoadError(null);
        }
      } catch (error) {
        if (!cancelled) {
          setPeople([]);
          setManageSupported(false);
          setLoadError(
            error?.detail || error?.message || intl.get("baas.users.loadError"),
          );
        }
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // instancesKey (not `instances`) avoids refetch when the array reference churns.
  }, [isInstanceScope, selectedInstance?.id, instancesKey, refreshKey]);

  // Live presence (SignalR heartbeat) — silent refresh of loggedIn dots only.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        let items = [];
        if (isInstanceScope) {
          if (!selectedInstance) return;
          const result = await fetchBaasUsers(selectedInstance);
          items = result.items;
        } else if (instancesKey) {
          const result = await fetchBaasUsers(null, { instances });
          items = result.items;
        } else {
          return;
        }
        if (cancelled) return;
        setPeople(aggregateBaasUsersByPerson(items));
      } catch {
        // Presence refresh is best-effort.
      }
    };
    const timer = setInterval(tick, 12_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [isInstanceScope, selectedInstance, instances, instancesKey]);

  const scopedPeople = useMemo(() => {
    if (isInstanceScope) {
      if (!selectedInstance) return [];
      const selectedId = String(selectedInstance.id);
      return people.filter((person) =>
        person.memberships.some((m) => String(m.instanceId) === selectedId),
      );
    }

    // Org Administração: Valut matrix / AllowGlobalAccess / super_user only.
    // Partner operators stay on /baas/instancia/usuarios.
    const matrixInstanceIds = new Set(
      instances
        .filter(
          (item) =>
            item?.allowGlobalAccess === true || item?.allow_global_access === true,
        )
        .map((item) => String(item.id)),
    );

    const valutOnly = people
      .map((person) => {
        const memberships = (person.memberships || []).filter(
          (m) =>
            m.role === "super_user" ||
            matrixInstanceIds.has(String(m.instanceId)) ||
            matrixInstanceIds.has(String(m.baasInstanceId)),
        );
        if (memberships.length === 0) return null;
        return { ...person, memberships };
      })
      .filter(Boolean);

    if (!instanceFilterId) return valutOnly;

    const filterId = String(instanceFilterId);
    return valutOnly.filter((person) =>
      person.memberships.some((m) => String(m.instanceId) === filterId),
    );
  }, [isInstanceScope, selectedInstance, people, instanceFilterId, instances]);

  const filterInstances = useMemo(() => {
    // Org filter: Valut matrix / AllowGlobalAccess cards only.
    const matrix = instances.filter(
      (item) =>
        item?.allowGlobalAccess === true || item?.allow_global_access === true,
    );
    const pool = matrix.length > 0 ? matrix : instances;
    const prod = pool.filter((item) => {
      const env = String(item?.environment || "").trim().toLowerCase();
      return item?.isProduction === true || env === "production" || env === "prod";
    });
    return (prod.length > 0 ? prod : pool).slice().sort((a, b) =>
      String(a.name ?? "").localeCompare(String(b.name ?? ""), undefined, {
        sensitivity: "base",
      }),
    );
  }, [instances]);

  const filteredPeople = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return scopedPeople;
    }

    return scopedPeople.filter((person) => {
      const instanceNames = person.memberships
        .map((m) => instanceNameById[m.instanceId] ?? "")
        .join(" ")
        .toLowerCase();
      const roles = person.memberships
        .map((m) => intl.get(roleLabelKey[m.role] ?? m.role))
        .join(" ")
        .toLowerCase();
      const documentDigits = String(person.nationalRegistration ?? "").replace(/\D/g, "");
      const queryDigits = query.replace(/\D/g, "");
      const isSuperUser = person.memberships.some((m) => m.role === "super_user");
      const name = String(person.name ?? "").toLowerCase();
      const email = String(person.email ?? "").toLowerCase();
      const instancesAllLabel = String(intl.get("baas.users.instancesAll") || "").toLowerCase();

      return (
        name.includes(query) ||
        email.includes(query) ||
        roles.includes(query) ||
        instanceNames.includes(query) ||
        (queryDigits && documentDigits.includes(queryDigits)) ||
        (isSuperUser && instancesAllLabel && instancesAllLabel.includes(query))
      );
    });
  }, [searchQuery, scopedPeople, instanceNameById]);

  const handleModalClose = () => {
    setCreateModalOpen(false);
    setEditingPerson(null);
  };

  const handleSaved = () => {
    setRefreshKey((current) => current + 1);
  };

  if (isLoading || usersLoading) {
    return <BaasTablePageSkeleton columns={isInstanceScope ? 8 : 9} />;
  }

  // Operador is not "read-only" (has transactions.write) — gate on users.manage.
  if (
    isInstanceScope &&
    !capsLoading &&
    !isMaster &&
    !can("users.manage")
  ) {
    return <Redirect to="/baas/inicio" />;
  }

  return (
    <div className="baas-users min-w-0 space-y-5">
      <header className="min-w-0">
        <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
          {intl.get("baas.nav.usuarios")}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {isInstanceScope
            ? intl.get(
                isHmlLane ? "baas.users.subtitleHml" : "baas.users.subtitle",
                {
                  instance: selectedInstance?.name ?? "",
                },
              )
            : intl.get("baas.users.subtitleOrg")}
        </p>
      </header>

      {isHmlLane ? (
        <div
          className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200 sm:flex-row sm:items-center sm:justify-between"
          role="status"
        >
          <p className="min-w-0 flex-1">
            {intl.get("baas.users.manageOnProdBanner")}
          </p>
          {homologPair.production?.id ? (
            <button
              type="button"
              className="btn-secondary shrink-0 !h-9"
              onClick={() => selectInstance(homologPair.production.id)}
            >
              {intl.get("baas.users.manageOnProdAction")}
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative max-w-md flex-1">
            <TbSearch
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
              aria-hidden
            />
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={intl.get("baas.users.searchPlaceholder")}
              className="h-10 w-full rounded-lg border border-gray-200 bg-white pl-10 pr-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100"
            />
          </div>
          {!isInstanceScope && filterInstances.length > 0 ? (
            <label className="flex shrink-0 items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <span className="sr-only">{intl.get("baas.users.filterInstanceLabel")}</span>
              <select
                value={instanceFilterId}
                onChange={(event) => setInstanceFilterId(event.target.value)}
                className="h-10 max-w-xs rounded-lg border border-gray-200 bg-white px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100"
                aria-label={intl.get("baas.users.filterInstanceLabel")}
              >
                <option value="">{intl.get("baas.users.filterInstanceAll")}</option>
                {filterInstances.map((instance) => (
                  <option key={instance.id} value={String(instance.id)}>
                    {instance.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>

        {canCreateUsers ? (
          <button
            type="button"
            className="btn-primary shrink-0 !h-10"
            onClick={() => setCreateModalOpen(true)}
          >
            <TbUserPlus size={18} aria-hidden />
            {intl.get("baas.users.addUser")}
          </button>
        ) : null}
      </div>

      {loadError ? (
        <div
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300"
          role="alert"
        >
          {loadError}
        </div>
      ) : null}

      <div className="app-card overflow-hidden">
        <div className="overflow-x-auto">
          {/*
            Column count must match header ↔ body (presence dot lives INSIDE NOME).
            Instance: NOME | DOC | EMAIL | FUNÇÃO | STATUS | ACESSO | ÚLTIMO | AÇÕES?
            Org:      + INSTÂNCIA before FUNÇÃO.
          */}
          <table className="w-full min-w-[64rem] table-fixed text-left text-sm">
            <colgroup>
              <col className="w-[14%]" />
              <col className="w-[11%]" />
              <col className="w-[18%]" />
              {!isInstanceScope ? <col className="w-[12%]" /> : null}
              <col className="w-[10%]" />
              <col className="w-[8%]" />
              <col className="w-[12%]" />
              <col className="w-[11%]" />
              {canManageUsers ? <col className="w-[10%]" /> : null}
            </colgroup>
            <thead className="border-b border-gray-100 bg-gray-50/80 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:border-brand-dark-border dark:bg-brand-dark-elevated/50 dark:text-gray-400">
              <tr>
                <th className="px-4 py-3">{intl.get("baas.users.columns.name")}</th>
                <th className="px-4 py-3">{intl.get("baas.users.columns.document")}</th>
                <th className="px-4 py-3">{intl.get("baas.users.columns.email")}</th>
                {!isInstanceScope ? (
                  <th className="px-4 py-3">{intl.get("baas.users.columns.instance")}</th>
                ) : null}
                <th className="px-4 py-3">{intl.get("baas.users.columns.role")}</th>
                <th className="px-4 py-3">{intl.get("baas.users.columns.status")}</th>
                <th className="px-4 py-3">{intl.get("baas.users.columns.access")}</th>
                <th className="px-4 py-3">{intl.get("baas.users.columns.lastAccess")}</th>
                {canManageUsers ? (
                  <th className="px-4 py-3 whitespace-nowrap">
                    {intl.get("baas.users.columns.actions")}
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-brand-dark-border">
              {filteredPeople.map((person) => {
                const scopeInstanceId = isInstanceScope
                  ? selectedInstance?.id
                  : instanceFilterId || null;
                const memberships = scopeInstanceId
                  ? person.memberships.filter(
                      (m) => String(m.instanceId) === String(scopeInstanceId),
                    )
                  : person.memberships;
                const role = primaryRole(memberships.length ? memberships : person.memberships);
                const status = primaryStatus(
                  memberships.length ? memberships : person.memberships,
                );
                const accessStatus = person.accessStatus || "pending";
                const instanceChips =
                  memberships.length > 0 ? memberships : person.memberships;

                return (
                  <tr
                    key={person.key}
                    className="hover:bg-gray-50/60 dark:hover:bg-brand-dark-elevated/30"
                  >
                    <td className="max-w-0 px-4 py-3 font-medium text-brand-dark dark:text-gray-100">
                      <span className="flex min-w-0 items-center gap-2">
                        <BaasLoggedInDot
                          loggedIn={Boolean(person.loggedIn)}
                          hasAccount={person.hasAccount === true}
                          accessStatus={accessStatus}
                        />
                        <span className="min-w-0 flex-1 truncate" title={person.name}>
                          {person.name}
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-600 dark:text-gray-400">
                      {formatDocumentDisplay(person.nationalRegistration)}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                      <span className="block truncate" title={person.email}>
                        {person.email}
                      </span>
                    </td>
                    {!isInstanceScope ? (
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {role === "super_user" && !instanceFilterId ? (
                            <span
                              className="inline-flex rounded-md bg-[#fff8ec] px-2 py-0.5 text-xs font-medium text-[#b87a12] dark:bg-brand-dark-surface dark:text-[#e7a73f]"
                              title={intl.get("baas.users.instancesAllHint")}
                            >
                              {intl.get("baas.users.instancesAll")}
                            </span>
                          ) : (
                            instanceChips.map((membership) => (
                              <span
                                key={membership.id}
                                className="inline-flex rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-brand-dark-elevated dark:text-gray-300"
                              >
                                {instanceNameById[membership.instanceId] ??
                                  membership.baasInstanceId ??
                                  membership.instanceId}
                              </span>
                            ))
                          )}
                        </div>
                      </td>
                    ) : null}
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                      {intl.get(roleLabelKey[role] ?? role)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusStyles[status] ?? statusStyles.suspended}`}
                      >
                        {intl.get(`baas.users.status.${status}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${accessStatusStyles[accessStatus] ?? accessStatusStyles.pending}`}
                        title={intl.get(`baas.users.access.hint.${accessStatus}`)}
                      >
                        {intl.get(`baas.users.access.status.${accessStatus}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                      {formatLastAccess(person.lastAccess)}
                    </td>
                    {canManageUsers ? (
                      <td className="px-4 py-3 whitespace-nowrap">
                        <GridHistoryDetailButton
                          onClick={() => setEditingPerson(person)}
                        />
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredPeople.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            {loadError
              ? intl.get("baas.users.loadError")
              : searchQuery.trim()
                ? intl.get("baas.users.emptySearch")
                : intl.get(isInstanceScope ? "baas.users.empty" : "baas.users.emptyOrg")}
          </p>
        ) : null}
      </div>

      {canManageUsers ? (
        <BaasUserCreateModal
          open={createModalOpen || Boolean(editingPerson)}
          person={editingPerson}
          scope={isInstanceScope ? "instance" : "org"}
          fixedInstance={isInstanceScope ? selectedInstance : null}
          onClose={handleModalClose}
          onSaved={handleSaved}
        />
      ) : null}
    </div>
  );
};
